import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Building2, Upload, FileText, CheckCircle, X } from "lucide-react";
import { updateBusinessDetails, checkProfileCompletion } from "../apis/businessDetails";

const BusinessDetailsModal = ({ isOpen, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    legal_business_name: "",
    address: "",
    pincode: "",
    business_email: "",
    city: "",
    state: "",
    tax_id: "",
    business_document_type: "",
  });
  const [documentFile, setDocumentFile] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const documentTypes = [
    "Certificate of Incorporation",
    "Business Registration Certificate",
    "Trade License",
    "GST / VAT Registration Certificate",
    "Partnership Deed",
  ];

  useEffect(() => {
    if (isOpen) {
      // Check if already completed
      checkProfileStatus();
    }
  }, [isOpen]);

  const checkProfileStatus = async () => {
    try {
      const response = await checkProfileCompletion();
      if (response.data.profile_completed) {
        // Already completed, close modal and proceed
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      }
    } catch (err) {
      console.error("Error checking profile status:", err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setError("Please upload a PDF, DOC, DOCX, or image file (JPG/PNG)");
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        return;
      }

      setDocumentFile(file);
      setError("");

      // Create preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setDocumentPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setDocumentPreview(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate all fields
    if (
      !form.legal_business_name ||
      !form.address ||
      !form.pincode ||
      !form.business_email ||
      !form.city ||
      !form.state ||
      !form.tax_id ||
      !form.business_document_type
    ) {
      setError("All fields are required");
      toast.error("Please fill in all fields");
      return;
    }

    if (!documentFile) {
      setError("Please upload a business document");
      toast.error("Business document is required");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.business_email)) {
      setError("Please enter a valid business email address");
      toast.error("Invalid email format");
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("legal_business_name", form.legal_business_name);
      formData.append("address", form.address);
      formData.append("pincode", form.pincode);
      formData.append("business_email", form.business_email);
      formData.append("city", form.city);
      formData.append("state", form.state);
      formData.append("tax_id", form.tax_id);
      formData.append("business_document_type", form.business_document_type);
      formData.append("business_document", documentFile);

      await updateBusinessDetails(formData);
      toast.success("Business details submitted successfully!");
      
      // Update user in localStorage
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem("user", JSON.stringify({ ...user, profile_completed: true }));
      
      // Call onSuccess callback to proceed with subscription
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      const message =
        err.response?.data?.error || "Failed to submit business details";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#048AAD]/10 rounded-xl">
              <Building2 className="text-[#048AAD]" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Complete Your Business Profile
              </h2>
              <p className="text-sm text-gray-600">
                Required before purchasing a subscription
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
            <FileText size={18} className="text-blue-600" />
            <p className="text-sm text-blue-800">
              <strong>Required:</strong> All fields must be completed to proceed with subscription.
            </p>
          </div>

          {error && (
            <div className="mb-6 flex items-center gap-2 bg-rose-50 border-l-4 border-rose-500 rounded-md p-4 text-rose-700 text-sm font-medium">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* Legal Business Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Legal Business Name <span className="text-rose-500">*</span>
              </label>
              <input
                name="legal_business_name"
                type="text"
                placeholder="Enter your legal business name"
                value={form.legal_business_name}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Address <span className="text-rose-500">*</span>
              </label>
              <textarea
                name="address"
                placeholder="Enter your business address"
                value={form.address}
                onChange={handleChange}
                required
                rows={3}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900 resize-none"
              />
            </div>

            {/* City, State, Pincode Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  City <span className="text-rose-500">*</span>
                </label>
                <input
                  name="city"
                  type="text"
                  placeholder="City"
                  value={form.city}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  State <span className="text-rose-500">*</span>
                </label>
                <input
                  name="state"
                  type="text"
                  placeholder="State"
                  value={form.state}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Pincode <span className="text-rose-500">*</span>
                </label>
                <input
                  name="pincode"
                  type="text"
                  placeholder="Pincode"
                  value={form.pincode}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
                />
              </div>
            </div>

            {/* Business Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Business Email <span className="text-rose-500">*</span>
              </label>
              <input
                name="business_email"
                type="email"
                placeholder="Enter your business email"
                value={form.business_email}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
              />
            </div>

            {/* Tax ID */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tax ID <span className="text-rose-500">*</span>
              </label>
              <input
                name="tax_id"
                type="text"
                placeholder="Enter your Tax ID"
                value={form.tax_id}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
              />
            </div>

            {/* Document Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Business Document Type <span className="text-rose-500">*</span>
              </label>
              <select
                name="business_document_type"
                value={form.business_document_type}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
              >
                <option value="">Select document type</option>
                {documentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Document Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Business Document <span className="text-rose-500">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-[#01CC54] transition">
                <input
                  type="file"
                  id="document-upload"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="hidden"
                  required
                />
                <label
                  htmlFor="document-upload"
                  className="flex flex-col items-center justify-center cursor-pointer"
                >
                  <Upload
                    size={32}
                    className="text-gray-400 mb-2 hover:text-[#01CC54] transition"
                  />
                  <p className="text-xs font-medium text-gray-700 mb-1">
                    {documentFile
                      ? documentFile.name
                      : "Click to upload or drag and drop"}
                  </p>
                  <p className="text-xs text-gray-500">
                    PDF, DOC, DOCX, JPG, or PNG (Max 10MB)
                  </p>
                </label>
              </div>

              {documentPreview && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-700 mb-2">
                    Document Preview:
                  </p>
                  <img
                    src={documentPreview}
                    alt="Document preview"
                    className="max-w-full h-auto rounded-lg border border-gray-200 max-h-48"
                  />
                </div>
              )}

              {documentFile && !documentPreview && (
                <div className="mt-4 flex items-center gap-2 text-xs text-gray-600">
                  <FileText size={16} />
                  <span>{documentFile.name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`
                flex-1 flex items-center justify-center gap-2
                rounded-xl py-3 text-sm font-bold text-white
                bg-[#048AAD]
                shadow-lg transition
                hover:scale-105 hover:shadow-xl hover:bg-[#048AAD]/90
                focus:outline-none focus:ring-2 focus:ring-[#048AAD]/40
                disabled:opacity-60 disabled:cursor-not-allowed
              `}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  <span>Complete & Continue</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessDetailsModal;

